import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@cloudscape-design/components/modal";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Alert from "@cloudscape-design/components/alert";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import CodeEditor from "@cloudscape-design/components/code-editor";
import "ace-builds/css/ace.css";
import "ace-builds/css/theme/dawn.css";
import "ace-builds/css/theme/tomorrow_night_bright.css";
import "./GatewayTargetModal.css";

// Import ace and configure it
import ace from "ace-builds";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-dawn";
import "ace-builds/src-noconflict/theme-tomorrow_night_bright";
import "ace-builds/src-noconflict/worker-json";

// Set the base path for ace workers
ace.config.set("basePath", "https://cdn.jsdelivr.net/npm/ace-builds@1.32.2/src-noconflict/");
ace.config.set("modePath", "https://cdn.jsdelivr.net/npm/ace-builds@1.32.2/src-noconflict/");
ace.config.set("themePath", "https://cdn.jsdelivr.net/npm/ace-builds@1.32.2/src-noconflict/");
ace.config.set("workerPath", "https://cdn.jsdelivr.net/npm/ace-builds@1.32.2/src-noconflict/");

const TEMPLATE_TOOL_SCHEMA = `[
  {
    "name": "tool_name",
    "description": "Description of what this tool does",
    "inputSchema": {
      "type": "object",
      "properties": {
        "param1": {
          "type": "string",
          "description": "Description of parameter 1"
        },
        "param2": {
          "type": "integer",
          "description": "Description of parameter 2"
        }
      },
      "required": ["param1"]
    }
  }
]`;

function GatewayTargetModal({ mode, target, onSubmit, onCancel, visible }) {
  const { t } = useTranslation();
  
  const getInitialFormData = (targetData, editMode) => {
    if (editMode && targetData) {
      return {
        name: targetData.name || "",
        description: targetData.description || "",
        lambda_arn: targetData.lambda_arn || "",
        tool_schema: targetData.tool_schema ? JSON.stringify(targetData.tool_schema, null, 2) : "[]",
      };
    }
    return {
      name: "",
      description: "",
      lambda_arn: "",
      tool_schema: TEMPLATE_TOOL_SCHEMA,
    };
  };
  
  const [formData, setFormData] = useState(() => getInitialFormData(target, mode === "edit"));
  
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [aceLoading, setAceLoading] = useState(true);
  const [editorPreferences, setEditorPreferences] = useState({});

  useEffect(() => {
    // Ace is loaded synchronously via imports
    setAceLoading(false);
  }, []);

  useEffect(() => {
    // Reset form when modal opens or target changes
    if (visible && mode === "edit" && target) {
      setFormData({
        name: target.name || "",
        description: target.description || "",
        lambda_arn: target.lambda_arn || "",
        tool_schema: target.tool_schema ? JSON.stringify(target.tool_schema, null, 2) : "[]",
      });
      setErrors({});
      setApiError("");
    } else if (visible && mode === "create") {
      setFormData(getInitialFormData(null, false));
      setErrors({});
      setApiError("");
    }
  }, [visible, mode, target]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t("gateway-targets.errors.name_required");
    } else if (formData.name.length > 100) {
      newErrors.name = t("gateway-targets.errors.name_too_long");
    } else if (!/^[a-zA-Z0-9-]+$/.test(formData.name)) {
      newErrors.name = t("gateway-targets.errors.name_invalid");
    }
    
    if (!formData.description.trim()) {
      newErrors.description = t("gateway-targets.errors.description_required");
    } else if (formData.description.length > 200) {
      newErrors.description = t("gateway-targets.errors.description_too_long");
    }
    
    if (!formData.lambda_arn.trim()) {
      newErrors.lambda_arn = t("gateway-targets.errors.lambda_arn_required");
    } else if (!formData.lambda_arn.startsWith("arn:aws:lambda:")) {
      newErrors.lambda_arn = t("gateway-targets.errors.lambda_arn_invalid");
    }
    
    if (formData.tool_schema.trim()) {
      try {
        const parsed = JSON.parse(formData.tool_schema);
        if (!Array.isArray(parsed)) {
          newErrors.tool_schema = t("gateway-targets.errors.tool_schema_not_array");
        }
      } catch {
        newErrors.tool_schema = t("gateway-targets.errors.tool_schema_invalid");
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    setApiError("");
    
    try {
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        lambda_arn: formData.lambda_arn.trim(),
        tool_schema: formData.tool_schema.trim() ? JSON.parse(formData.tool_schema) : [],
      };
      
      await onSubmit(submitData, mode === "edit" ? target.target_id : null);
    } catch (error) {
      setApiError(error.message || t("gateway-targets.errors.submit_failed"));
      setSubmitting(false);
    }
  };

  return (
    <div className="gateway-target-modal">
      <Modal
        visible={visible}
        onDismiss={onCancel}
        header={mode === "create" ? t("gateway-targets.modal.create_title") : t("gateway-targets.modal.edit_title")}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onCancel} disabled={submitting}>
                {t("gateway-targets.modal.cancel")}
              </Button>
              <Button variant="primary" onClick={handleSubmit} loading={submitting}>
                {mode === "create" ? t("gateway-targets.modal.create_button") : t("gateway-targets.modal.save_button")}
              </Button>
            </SpaceBetween>
          </Box>
        }
        size="large"
      >
        <div className="gateway-target-modal-content">
          <ColumnLayout columns={1}>
            <SpaceBetween size="l">
              {apiError && <Alert type="error">{apiError}</Alert>}
              
              <FormField 
                label={t("gateway-targets.form.name")} 
                errorText={errors.name} 
                constraintText={t("gateway-targets.form.name_constraint")}
                stretch
              >
                <Input 
                  value={formData.name} 
                  onChange={({ detail }) => setFormData({ ...formData, name: detail.value })} 
                  placeholder={t("gateway-targets.form.name_placeholder")} 
                  disabled={submitting || mode === "edit"} 
                />
              </FormField>
              
              <FormField 
                label={t("gateway-targets.form.description")} 
                errorText={errors.description} 
                constraintText={t("gateway-targets.form.description_constraint")}
                stretch
              >
                <Input 
                  value={formData.description} 
                  onChange={({ detail }) => setFormData({ ...formData, description: detail.value })} 
                  placeholder={t("gateway-targets.form.description_placeholder")} 
                  disabled={submitting} 
                />
              </FormField>
              
              <FormField 
                label={t("gateway-targets.form.lambda_arn")} 
                errorText={errors.lambda_arn}
                description={t("gateway-targets.form.lambda_arn_description")}
                stretch
              >
                <Input 
                  value={formData.lambda_arn} 
                  onChange={({ detail }) => setFormData({ ...formData, lambda_arn: detail.value })} 
                  placeholder={t("gateway-targets.form.lambda_arn_placeholder")} 
                  disabled={submitting} 
                />
              </FormField>
              
              <FormField 
                label={t("gateway-targets.form.tool_schema")} 
                errorText={errors.tool_schema}
                description={t("gateway-targets.form.tool_schema_description")}
                stretch
              >
                <CodeEditor
                  ace={ace}
                  language="json"
                  value={formData.tool_schema}
                  onChange={({ detail }) => setFormData({ ...formData, tool_schema: detail.value })}
                  preferences={editorPreferences}
                  onPreferencesChange={({ detail }) => setEditorPreferences(detail)}
                  loading={aceLoading}
                  editorContentHeight={300}
                  themes={{ light: ["dawn"], dark: ["tomorrow_night_bright"] }}
                  i18nStrings={{
                    loadingState: t("app.common.loading"),
                    errorState: "Error loading editor",
                    errorStateRecovery: "Retry",
                    editorGroupAriaLabel: "Code editor",
                    statusBarGroupAriaLabel: "Status bar",
                    cursorPosition: (row, column) => `Ln ${row}, Col ${column}`,
                    errorsTab: "Errors",
                    warningsTab: "Warnings",
                    preferencesButtonAriaLabel: "Preferences",
                    paneCloseButtonAriaLabel: "Close",
                    preferencesModalHeader: "Preferences",
                    preferencesModalCancel: "Cancel",
                    preferencesModalConfirm: "Confirm",
                    preferencesModalWrapLines: "Wrap lines",
                    preferencesModalTheme: "Theme",
                    preferencesModalLightThemes: "Light themes",
                    preferencesModalDarkThemes: "Dark themes",
                  }}
                />
              </FormField>
            </SpaceBetween>
          </ColumnLayout>
        </div>
      </Modal>
    </div>
  );
}

export default GatewayTargetModal;
